import {
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type ISiteTextPreset,
  type IStyleParam,
  type IWixStyleFont,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import type { CustomCssVarsFn } from '@wix/yoshi-flow-editor';

export const fontValueFix = (
  fontFn: Function,
  textPresets: ISiteTextPreset,
): IWixStyleFont => {
  const { value: _, ...font } = fontFn({ textPresets });

  return {
    ...font,
  } as IWixStyleFont;
};

const booleanParam = (
  defaultValue: boolean,
): Omit<IStyleParam<StyleParamType.Boolean, boolean>, 'name'> => ({
  type: StyleParamType.Boolean,
  getDefaultValue: () => defaultValue,
});

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

const numberParam = (defaultValue: number): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue,
});

const numberParamWithoutInheritDesktop = (
  defaultValue: number,
): NumberStyleParam => ({
  inheritDesktop: false,
  ...numberParam(defaultValue),
});

const numberParamWithMobileDefault = (
  mobileDefaultValue: number,
  defaultValue: number,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ isMobile }) =>
    isMobile ? mobileDefaultValue : defaultValue,
  inheritDesktop: false,
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

const colorParam = (preset: string): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
});

const colorParamWithOpacity = (
  preset: string,
  opacity: number,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

const fontParam = (
  preset: keyof ISiteTextPreset,
  size: number,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ textPresets }) => fontValueFix(wixFontParam(preset, { size }), textPresets),
});

const fontParamWithMobileDefault = (
  preset: keyof ISiteTextPreset,
  mobileSize: number,
  size: number,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ textPresets, isMobile }) => fontValueFix(
    wixFontParam(preset, {
      size: isMobile ? mobileSize : size,
    }),
    textPresets,
  ),
});

const stylesParams = createStylesParams<{
  sectionTitleBookButtonVisibility: StyleParamType.Boolean;
  sectionSchedulingBookButtonVisibility: StyleParamType.Boolean;
  sectionContactBookButtonVisibility: StyleParamType.Boolean;
  sectionDescriptionBookButtonVisibility: StyleParamType.Boolean;
  sectionDetailsBookButtonVisibility: StyleParamType.Boolean;
  sectionGalleryBookButtonVisibility: StyleParamType.Boolean;
  sectionPluginBookButtonVisibility: StyleParamType.Boolean;
  sectionPolicyBookButtonVisibility: StyleParamType.Boolean;
  sectionTitleVisibility: StyleParamType.Boolean;
  sectionSchedulingVisibility: StyleParamType.Boolean;
  sectionContactVisibility: StyleParamType.Boolean;
  sectionDescriptionVisibility: StyleParamType.Boolean;
  sectionDetailsVisibility: StyleParamType.Boolean;
  sectionGalleryVisibility: StyleParamType.Boolean;
  sectionPluginVisibility: StyleParamType.Boolean;
  sectionPolicyVisibility: StyleParamType.Boolean;
  sectionTitleDivider: StyleParamType.Boolean;
  sectionSchedulingDivider: StyleParamType.Boolean;
  sectionContactDivider: StyleParamType.Boolean;
  sectionDescriptionDivider: StyleParamType.Boolean;
  sectionDetailsDivider: StyleParamType.Boolean;
  sectionGalleryDivider: StyleParamType.Boolean;
  sectionPluginDivider: StyleParamType.Boolean;
  sectionPolicyDivider: StyleParamType.Boolean;
  headerVisibility: StyleParamType.Boolean;
  headerTitleVisibility: StyleParamType.Boolean;
  headerImageVisibility: StyleParamType.Boolean;
  headerBookButtonVisibility: StyleParamType.Boolean;
  sidebarVisibility: StyleParamType.Boolean;
  onlineBadgeVisibility: StyleParamType.Boolean;
  serviceTitleVisibility: StyleParamType.Boolean;
  serviceTaglineVisibility: StyleParamType.Boolean;
  detailsDurationVisibility: StyleParamType.Boolean;
  detailsPriceVisibility: StyleParamType.Boolean;
  detailsLocationVisibility: StyleParamType.Boolean;
  businessEmailVisibility: StyleParamType.Boolean;
  businessPhoneNumberVisibility: StyleParamType.Boolean;
  businessAddressVisibility: StyleParamType.Boolean;
  locationNameVisibility: StyleParamType.Boolean;
  scheduleDurationVisibility: StyleParamType.Boolean;
  scheduleStaffVisibility: StyleParamType.Boolean;
  headerHeight: StyleParamType.Number;
  headerSidePadding: StyleParamType.Number;
  headerImageOpacity: StyleParamType.Number;
  headerImageBackgroundColor: StyleParamType.Color;
  headerBackgroundColor: StyleParamType.Color;
  headerTitleFont: StyleParamType.Font;
  headerTitleColor: StyleParamType.Color;
  bodyBackgroundColor: StyleParamType.Color;
  bodyServiceTitleFont: StyleParamType.Font;
  bodyServiceTitleColor: StyleParamType.Color;
  bodyServiceTaglineFont: StyleParamType.Font;
  bodyServiceTaglineColor: StyleParamType.Color;
  bodySectionTitleFont: StyleParamType.Font;
  bodySectionTitleColor: StyleParamType.Color;
  bodyTextFont: StyleParamType.Font;
  bodyTextColor: StyleParamType.Color;
  courseAvailabilityTextFont: StyleParamType.Font;
  courseAvailabilityTextColor: StyleParamType.Color;
  bodyDividerWidth: StyleParamType.Number;
  bodyDividerColor: StyleParamType.Color;
  sidebarBackgroundColor: StyleParamType.Color;
  sidebarTitleColor: StyleParamType.Color;
  sidebarTitleFont: StyleParamType.Font;
  sidebarTextColor: StyleParamType.Color;
  sidebarTextFont: StyleParamType.Font;
  sidebarServiceTitleColor: StyleParamType.Color;
  sidebarServiceTitleFont: StyleParamType.Font;
  sidebarServiceTaglineColor: StyleParamType.Color;
  sidebarServiceTaglineFont: StyleParamType.Font;
  sidebarButtonTextColor: StyleParamType.Color;
  sidebarButtonTextFont: StyleParamType.Font;
  sidebarButtonBackgroundColor: StyleParamType.Color;
  sidebarButtonBorderColor: StyleParamType.Color;
  sidebarButtonBorderWidth: StyleParamType.Number;
  sidebarButtonCornerRadius: StyleParamType.Number;
  sidebarButtonHoverTextColor: StyleParamType.Color;
  sidebarButtonHoverBackgroundColor: StyleParamType.Color;
  sidebarButtonHoverBorderColor: StyleParamType.Color;
  sidebarWidth: StyleParamType.Number;
  sidebarSidePadding: StyleParamType.Number;
  sidebarSideSpacing: StyleParamType.Number;
  sidebarSpaceFromTheTop: StyleParamType.Number;
  headerButtonTextColor: StyleParamType.Color;
  headerButtonTextFont: StyleParamType.Font;
  headerButtonBackgroundColor: StyleParamType.Color;
  headerButtonBorderColor: StyleParamType.Color;
  headerButtonBorderWidth: StyleParamType.Number;
  headerButtonCornerRadius: StyleParamType.Number;
  headerButtonHoverTextColor: StyleParamType.Color;
  headerButtonHoverBackgroundColor: StyleParamType.Color;
  headerButtonHoverBorderColor: StyleParamType.Color;
  bodyButtonTextColor: StyleParamType.Color;
  bodyButtonTextFont: StyleParamType.Font;
  bodyButtonBackgroundColor: StyleParamType.Color;
  bodyButtonBorderColor: StyleParamType.Color;
  bodyButtonBorderWidth: StyleParamType.Number;
  bodyButtonCornerRadius: StyleParamType.Number;
  bodyButtonHoverTextColor: StyleParamType.Color;
  bodyButtonHoverBackgroundColor: StyleParamType.Color;
  bodyButtonHoverBorderColor: StyleParamType.Color;
  bodySidePadding: StyleParamType.Number;
  bodySpaceBetweenSections: StyleParamType.Number;
  firstSectionSpaceFromTheTop: StyleParamType.Number;
  backgroundColor: StyleParamType.Color;
  detailsInnerPadding: StyleParamType.Number;
  detailsBoxFont: StyleParamType.Font;
  detailsBoxTextColor: StyleParamType.Color;
  detailsBoxColor: StyleParamType.Color;
  detailsBoxBorderColor: StyleParamType.Color;
  detailsBoxBorderWidth: StyleParamType.Number;
  detailsBoxBorderRadius: StyleParamType.Number;
  scheduleBoxPadding: StyleParamType.Number;
  scheduleBackgroundColor: StyleParamType.Color;
  scheduleBorderColor: StyleParamType.Color;
  scheduleBorderWidth: StyleParamType.Number;
  scheduleBorderRadius: StyleParamType.Number;
  scheduleDividerColor: StyleParamType.Color;
  scheduleDividerWidth: StyleParamType.Number;
  scheduleDatesFont: StyleParamType.Font;
  scheduleDatesColor: StyleParamType.Color;
  scheduleHoursFont: StyleParamType.Font;
  scheduleHoursColor: StyleParamType.Color;
  scheduleDetailsFont: StyleParamType.Font;
  scheduleDetailsColor: StyleParamType.Color;
  scheduleLinkFont: StyleParamType.Font;
  scheduleLinkColor: StyleParamType.Color;
  galleryMasonryItemBorderColor: StyleParamType.Color;
  galleryGridItemBorderColor: StyleParamType.Color;
  gallerySliderItemBorderColor: StyleParamType.Color;
  gallerySliderArrowColor: StyleParamType.Color;
  mobileServiceTitleFontSize: StyleParamType.Number;
  mobileServiceTaglineFontSize: StyleParamType.Number;
  mobileSectionTitleFontSize: StyleParamType.Number;
  mobileTextFontSize: StyleParamType.Number;
  mobileButtonTextSize: StyleParamType.Number;
  mobileHeaderTitleFontSize: StyleParamType.Number;
  mobileHeaderButtonTextSize: StyleParamType.Number;
  mobileDetailsBoxFontSize: StyleParamType.Number;
  mobileScheduleDatesFontSize: StyleParamType.Number;
  mobileScheduleHoursFontSize: StyleParamType.Number;
  mobileScheduleDetailsFontSize: StyleParamType.Number;
  mobileScheduleLinkFontSize: StyleParamType.Number;
}>({
  sectionTitleBookButtonVisibility: booleanParam(false),
  sectionSchedulingBookButtonVisibility: booleanParam(true),
  sectionContactBookButtonVisibility: booleanParam(false),
  sectionDescriptionBookButtonVisibility: booleanParam(false),
  sectionDetailsBookButtonVisibility: booleanParam(true),
  sectionGalleryBookButtonVisibility: booleanParam(false),
  sectionPluginBookButtonVisibility: booleanParam(false),
  sectionPolicyBookButtonVisibility: booleanParam(false),
  sectionTitleVisibility: booleanParam(true),
  sectionSchedulingVisibility: booleanParam(true),
  sectionContactVisibility: booleanParam(true),
  sectionDescriptionVisibility: booleanParam(true),
  sectionDetailsVisibility: booleanParam(true),
  sectionGalleryVisibility: booleanParam(true),
  sectionPluginVisibility: booleanParam(true),
  sectionPolicyVisibility: booleanParam(true),
  sectionTitleDivider: booleanParam(true),
  sectionSchedulingDivider: booleanParam(true),
  sectionContactDivider: booleanParam(true),
  sectionDescriptionDivider: booleanParam(true),
  sectionDetailsDivider: booleanParam(true),
  sectionGalleryDivider: booleanParam(true),
  sectionPluginDivider: booleanParam(true),
  sectionPolicyDivider: booleanParam(true),
  headerVisibility: booleanParam(true),
  headerTitleVisibility: booleanParam(false),
  headerImageVisibility: booleanParam(true),
  headerBookButtonVisibility: booleanParam(false),
  sidebarVisibility: booleanParam(false),
  onlineBadgeVisibility: booleanParam(true),
  serviceTitleVisibility: booleanParam(true),
  serviceTaglineVisibility: booleanParam(true),
  detailsDurationVisibility: booleanParam(true),
  detailsPriceVisibility: booleanParam(true),
  detailsLocationVisibility: booleanParam(true),
  businessEmailVisibility: booleanParam(true),
  businessPhoneNumberVisibility: booleanParam(true),
  businessAddressVisibility: booleanParam(true),
  locationNameVisibility: booleanParam(false),
  scheduleDurationVisibility: booleanParam(true),
  scheduleStaffVisibility: booleanParam(true),
  headerHeight: numberParamWithMobileDefault(220, 360),
  headerSidePadding: numberParamWithoutInheritDesktop(20),
  headerImageOpacity: numberParam(100),
  headerImageBackgroundColor: colorParam('color-5'),
  bodyBackgroundColor: colorParam('color-1'),
  headerBackgroundColor: colorParam('color-5'),
  headerTitleFont: fontParamWithMobileDefault('Page-title', 32, 40),
  headerTitleColor: colorParam('color-1'),
  bodySectionTitleFont: fontParam('Page-title', 20),
  bodySectionTitleColor: colorParam('color-5'),
  bodyTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  bodyTextColor: colorParam('color-5'),
  courseAvailabilityTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  courseAvailabilityTextColor: colorParam('color-5'),
  bodySidePadding: numberParamWithoutInheritDesktop(20),
  firstSectionSpaceFromTheTop: numberParamWithoutInheritDesktop(48),
  bodySpaceBetweenSections: numberParamWithoutInheritDesktop(32),
  sidebarBackgroundColor: colorParam('color-1'),
  sidebarTitleColor: colorParam('color-5'),
  sidebarTitleFont: fontParam('Page-title', 20),
  sidebarTextColor: colorParam('color-5'),
  sidebarTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  sidebarServiceTitleColor: colorParam('color-5'),
  sidebarServiceTitleFont: fontParamWithMobileDefault('Page-title', 32, 40),
  sidebarServiceTaglineColor: colorParam('color-5'),
  sidebarServiceTaglineFont: fontParamWithMobileDefault('Body-M', 14, 16),
  sidebarWidth: numberParam(30),
  sidebarSidePadding: numberParamWithoutInheritDesktop(20),
  sidebarSideSpacing: numberParamWithoutInheritDesktop(0),
  sidebarSpaceFromTheTop: numberParamWithoutInheritDesktop(48),
  backgroundColor: colorParam('color-5'),
  detailsInnerPadding: numberParamWithoutInheritDesktop(16),
  detailsBoxFont: fontParamWithMobileDefault('Body-M', 14, 16),
  detailsBoxTextColor: colorParam('color-5'),
  detailsBoxColor: colorParamWithOpacity('color-1', 0),
  detailsBoxBorderColor: colorParamWithOpacity('color-5', 0.2),
  detailsBoxBorderWidth: numberParam(1),
  sidebarButtonTextColor: colorParam('color-1'),
  sidebarButtonTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  sidebarButtonBackgroundColor: colorParam('color-8'),
  sidebarButtonBorderColor: colorParam('color-8'),
  sidebarButtonBorderWidth: numberParam(0),
  sidebarButtonCornerRadius: numberParam(1),
  sidebarButtonHoverTextColor: colorParam('color-1'),
  sidebarButtonHoverBackgroundColor: colorParam('color-8'),
  sidebarButtonHoverBorderColor: colorParam('color-8'),
  bodyButtonTextColor: colorParam('color-1'),
  bodyButtonTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  bodyButtonBackgroundColor: colorParamWithOpacity('color-8', 1),
  bodyButtonBorderColor: colorParamWithOpacity('color-8', 1),
  bodyButtonBorderWidth: numberParam(0),
  bodyButtonCornerRadius: numberParam(0),
  bodyButtonHoverTextColor: colorParam('color-1'),
  bodyButtonHoverBackgroundColor: colorParam('color-8'),
  bodyButtonHoverBorderColor: colorParam('color-8'),
  headerButtonTextColor: colorParam('color-5'),
  headerButtonTextFont: fontParamWithMobileDefault('Body-M', 14, 16),
  headerButtonBackgroundColor: colorParam('color-1'),
  headerButtonBorderColor: colorParam('color-1'),
  headerButtonBorderWidth: numberParam(0),
  headerButtonCornerRadius: numberParam(0),
  headerButtonHoverTextColor: colorParam('color-5'),
  headerButtonHoverBackgroundColor: colorParam('color-1'),
  headerButtonHoverBorderColor: colorParam('color-1'),
  bodyDividerWidth: numberParam(1),
  detailsBoxBorderRadius: numberParam(0),
  bodyDividerColor: colorParamWithOpacity('color-5', 0.2),
  bodyServiceTitleFont: fontParamWithMobileDefault('Page-title', 32, 40),
  bodyServiceTitleColor: colorParam('color-5'),
  bodyServiceTaglineFont: fontParamWithMobileDefault('Body-M', 14, 16),
  bodyServiceTaglineColor: colorParam('color-5'),
  scheduleBoxPadding: numberParamWithMobileDefault(20, 28),
  scheduleBackgroundColor: colorParamWithOpacity('color-1', 0),
  scheduleBorderColor: colorParamWithOpacity('color-5', 0.2),
  scheduleBorderWidth: numberParam(1),
  scheduleBorderRadius: numberParam(0),
  scheduleDividerColor: colorParamWithOpacity('color-5', 0.2),
  scheduleDividerWidth: numberParam(1),
  scheduleDatesFont: fontParamWithMobileDefault('Body-M', 14, 16),
  scheduleDatesColor: colorParam('color-5'),
  scheduleHoursFont: fontParamWithMobileDefault('Body-M', 14, 16),
  scheduleHoursColor: colorParam('color-5'),
  scheduleDetailsFont: fontParam('Body-M', 14),
  scheduleDetailsColor: colorParam('color-4'),
  scheduleLinkFont: fontParamWithMobileDefault('Body-M', 14, 16),
  scheduleLinkColor: colorParam('color-8'),
  galleryMasonryItemBorderColor: colorParamWithOpacity('color-5', 0.2),
  galleryGridItemBorderColor: colorParamWithOpacity('color-5', 0.2),
  gallerySliderItemBorderColor: colorParamWithOpacity('color-5', 0.2),
  gallerySliderArrowColor: colorParam('color-5'),
  mobileServiceTitleFontSize: numberParam(32),
  mobileServiceTaglineFontSize: numberParam(14),
  mobileSectionTitleFontSize: numberParam(20),
  mobileTextFontSize: numberParam(14),
  mobileButtonTextSize: numberParam(14),
  mobileHeaderTitleFontSize: numberParam(32),
  mobileHeaderButtonTextSize: numberParam(14),
  mobileDetailsBoxFontSize: numberParam(14),
  mobileScheduleDatesFontSize: numberParam(14),
  mobileScheduleHoursFontSize: numberParam(14),
  mobileScheduleDetailsFontSize: numberParam(14),
  mobileScheduleLinkFontSize: numberParam(14),
});

export default stylesParams;

// noinspection JSUnusedGlobalSymbols
export const customCssVars: CustomCssVarsFn = ({ styleParams }) => {
  const displayStyle = (
    booleanStyleParam: keyof typeof stylesParams,
    visibleDisplay = 'block',
  ) => (styleParams.booleans[booleanStyleParam] ? visibleDisplay : 'none');

  const calculateMargin = (booleanStyleParam: keyof typeof stylesParams) =>
    styleParams.booleans[booleanStyleParam]
      ? styleParams.numbers.bodySpaceBetweenSections
      : 0;

  return {
    headerDisplay: displayStyle('headerVisibility', 'flex'),
    headerTitleDisplay: displayStyle('headerTitleVisibility'),
    headerImageDisplay: displayStyle('headerImageVisibility'),
    headerBookButtonDisplay: displayStyle('headerBookButtonVisibility'),
    sidebarDisplay: displayStyle('sidebarVisibility'),
    onlineBadgeDisplay: displayStyle('onlineBadgeVisibility'),
    serviceTitleDisplay: displayStyle('serviceTitleVisibility'),
    serviceTaglineDisplay: displayStyle('serviceTaglineVisibility'),
    detailsDurationDisplay: displayStyle('detailsDurationVisibility'),
    detailsPriceDisplay: displayStyle('detailsPriceVisibility'),
    detailsLocationDisplay: displayStyle('detailsLocationVisibility'),
    businessEmailDisplay: displayStyle('businessEmailVisibility'),
    businessPhoneNumberDisplay: displayStyle('businessPhoneNumberVisibility'),
    businessAddressDisplay: displayStyle('businessAddressVisibility'),
    locationNameDisplay: displayStyle('locationNameVisibility'),
    scheduleDurationDisplay: displayStyle('scheduleDurationVisibility'),
    scheduleStaffDisplay: displayStyle('scheduleStaffVisibility'),
    sectionTitleBookButtonDisplay: displayStyle(
      'sectionTitleBookButtonVisibility',
    ),
    sectionSchedulingBookButtonDisplay: displayStyle(
      'sectionSchedulingBookButtonVisibility',
    ),
    sectionContactBookButtonDisplay: displayStyle(
      'sectionContactBookButtonVisibility',
    ),
    sectionDescriptionBookButtonDisplay: displayStyle(
      'sectionDescriptionBookButtonVisibility',
    ),
    sectionDetailsBookButtonDisplay: displayStyle(
      'sectionDetailsBookButtonVisibility',
    ),
    sectionGalleryBookButtonDisplay: displayStyle(
      'sectionGalleryBookButtonVisibility',
    ),
    sectionPluginBookButtonDisplay: displayStyle(
      'sectionPluginBookButtonVisibility',
    ),
    sectionPolicyBookButtonDisplay: displayStyle(
      'sectionPolicyBookButtonVisibility',
    ),
    sectionTitleDisplay: displayStyle('sectionTitleVisibility'),
    sectionSchedulingDisplay: displayStyle('sectionSchedulingVisibility'),
    sectionContactDisplay: displayStyle('sectionContactVisibility'),
    sectionDescriptionDisplay: displayStyle('sectionDescriptionVisibility'),
    sectionDetailsDisplay: displayStyle('sectionDetailsVisibility'),
    sectionGalleryDisplay: displayStyle('sectionGalleryVisibility'),
    sectionPluginDisplay: displayStyle('sectionPluginVisibility'),
    sectionPolicyDisplay: displayStyle('sectionPolicyVisibility'),
    SectionTitleDividerDisplay: displayStyle('sectionTitleDivider'),
    sectionSchedulingDividerDisplay: displayStyle('sectionSchedulingDivider'),
    sectionContactDividerDisplay: displayStyle('sectionContactDivider'),
    sectionDescriptionDividerDisplay: displayStyle('sectionDescriptionDivider'),
    sectionDetailsDividerDisplay: displayStyle('sectionDetailsDivider'),
    sectionGalleryDividerDisplay: displayStyle('sectionGalleryDivider'),
    sectionPluginDividerDisplay: displayStyle('sectionPluginDivider'),
    sectionPolicyDividerDisplay: displayStyle('sectionPolicyDivider'),
    sectionTitleMargin: calculateMargin('sectionTitleDivider'),
    sectionSchedulingMargin: calculateMargin('sectionSchedulingDivider'),
    sectionContactMargin: calculateMargin('sectionContactDivider'),
    sectionDescriptionMargin: calculateMargin('sectionDescriptionDivider'),
    sectionDetailsMargin: calculateMargin('sectionDetailsDivider'),
    sectionGalleryMargin: calculateMargin('sectionGalleryDivider'),
    sectionPluginMargin: calculateMargin('sectionPluginDivider'),
    sectionPolicyMargin: calculateMargin('sectionPolicyDivider'),
  };
};
